import qs from 'qs'
import { OAUTH_SERVICE_URL } from '../../config'
import hrefType from 'href-type'

const OAUTH_SERVICE_SUPPORTED_SLUGS = [
  'personas-facebook-ads',
  'personas-website-facebook-ads',
  'facebook-custom-audiences',
  'adwords-remarketing-lists',
  'help-scout',
  'zendesk'
]

// this currently matches OAUTH_SERVICE_SUPPORTED_SLUGS but it will
// change as new providers are added
// TODO: use `sourceMetadata.features.accounts` to determine this
const OAUTH_PROVIDERS_WITH_ACCOUNTS = [
  'personas-facebook-ads',
  'personas-website-facebook-ads',
  'facebook-custom-audiences',
  'adwords-remarketing-lists'
]

const SLUGS_TO_PROVIDERS = {
  'personas-facebook-ads': 'facebook',
  'personas-website-facebook-ads': 'facebook',
  'facebook-custom-audiences': 'facebook',
  'adwords-remarketing-lists': 'google',
  'help-scout': 'help-scout',
  zendesk: 'zendesk'
}

export const getProviderBySlug = (metadataSlug: string) => SLUGS_TO_PROVIDERS[metadataSlug]
export const isModernOAuthServiceSupported = (metadataSlug: string) =>
  OAUTH_SERVICE_SUPPORTED_SLUGS.includes(metadataSlug)
export const hasAccounts = (metadataSlug: string) => OAUTH_PROVIDERS_WITH_ACCOUNTS.includes(metadataSlug)

function toAbsoluteUrl(url: string) {
  if (hrefType(url) === 'absolute') {
    return url
  }

  const { host, protocol } = window.location
  return `${protocol}//${host}${url}`
}

interface OAuthUrlArgs {
  provider: string
  redirect: string
  workspaceSlug: string
}

/**
 * Function that generates an oauth url intended to return the access tokens in the querystring upon successful authorization
 * This allows us to oauth _before_ creating the integration
 */
export function composeOAuthUrl({ provider, redirect, workspaceSlug }: OAuthUrlArgs) {
  const query = qs.stringify({
    redirect: toAbsoluteUrl(redirect),
    workspaceSlug
  })

  return `${OAUTH_SERVICE_URL}/legacy/connect/source/${provider}?${query}`
}

interface SourceOAuthUrlArgs {
  from?: string
  redirect: string
  sourceMetadataSlug: string
  sourceSlug: string
  workspaceSlug: string
}

export function composeSourceOAuthUrl({
  from,
  redirect,
  sourceMetadataSlug,
  sourceSlug,
  workspaceSlug
}: SourceOAuthUrlArgs) {
  const provider = sourceMetadataSlug
  const query = qs.stringify({
    // TODO: deprecate `from`
    from,
    redirect: toAbsoluteUrl(redirect),
    sourceMetadataSlug,
    sourceSlug,
    workspaceSlug
  })

  return `${OAUTH_SERVICE_URL}/legacy/connect/source/${provider}?${query}`
}

interface DestinationOAuthUrlArgs {
  destinationSlug: string
  redirect: string
  sourceSlug: string
  workspaceSlug: string
}

export function composeDestinationOAuthUrl({
  destinationSlug,
  redirect,
  sourceSlug,
  workspaceSlug
}: DestinationOAuthUrlArgs) {
  const provider = destinationSlug
  const query = qs.stringify({
    redirect: toAbsoluteUrl(redirect),
    sourceSlug,
    workspaceSlug,
    destinationSlug
  })

  return `${OAUTH_SERVICE_URL}/legacy/connect/destination/${provider}?${query}`
}

interface UserProfileUrlArgs {
  accessToken?: string
  destinationSlug?: string
  provider: string
  refreshToken?: string
  sourceSlug: string
  workspaceId: string
}

export function composeUserProfileUrl({
  accessToken,
  destinationSlug,
  provider,
  refreshToken,
  sourceSlug,
  workspaceId
}: UserProfileUrlArgs) {
  if (isModernOAuthServiceSupported(provider)) {
    const oauthProvider = getProviderBySlug(provider)
    return `${OAUTH_SERVICE_URL}/me/${oauthProvider}?${qs.stringify({
      accessToken,
      refreshToken
    })}`
  }

  const query = qs.stringify({
    destinationSlug,
    sourceSlug,
    workspaceId
  })

  return `${OAUTH_SERVICE_URL}/legacy/user-profile/${provider}?${query}`
}
